import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { toast } from "react-toastify";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { Row, Col, Label, Input } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchVehiclesDocumentsData } from "../../store/vehicles-documents/actions";
import { fetchVehiclesCategoriesData } from "../../store/vehicles-categories/actions";
import moment from 'moment';

const DEFAULT_PAGE_SIZE = 10;

const VehiclesDocument = props => {
  const dispatch = useDispatch();

  const { data, loading, error } = useSelector(state => state.vehiclesDocuments);
  const categoriesState = useSelector(state => state.vehiclesCategories);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [vehicleCategoryId, setVehicleCategoryId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDriverDocuments, setSelectedDriverDocuments] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [remarks, setRemarks] = useState({});

  const toggleDocSelection = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved": return "success";
      case "rejected": return "danger";
      case "pending": return "warning";
      default: return "secondary";
    }
  };

  const handleRemarkChange = (docId, value) => {
    setRemarks(prev => ({
      ...prev,
      [docId]: value
    }));
  };
  
  const areAllApproved = () => {
    return selectedDriverDocuments.documents?.every(item => item.status === "approved") || false;
  };

  const isImage = (url) => {
    return /\.(jpeg|jpg|png|gif|bmp|webp)$/i.test(url);
  };

  const getFileExtension = (url) => {
    return url?.split('.').pop() || 'file';
  };

  const handleViewDocuments = (driverRow) => {
    setSelectedDriverDocuments(driverRow || []);
    setSelectedDocIds([]);
    setModalOpen(true);
  };

  const handleActionClick = async (action, driverId, vehicleId) => {
    if (selectedDocIds.length === 0) {
      toast.error('Please select at least one document.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const selectedRemarks = selectedDocIds.map(id => ({
      document_id: id,
      remark: remarks[id] || ""
    }));

    const payload = {
      action: action,
      document_ids: selectedRemarks,
      userId: driverId,
      vehicle_id: vehicleId
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(process.env.REACT_APP_WEB_APP_URL + "/api/web/update-vehicles-documents", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status == 200) {
        toast.success('Documents update successfully', {
          position: "top-right",
          autoClose: 3000,
        });

        setModalOpen(false);
        dispatch(
          fetchVehiclesDocumentsData({
            page: pageIndex + 1,
            limit: pageSize,
            vehicleCategoryId: vehicleCategoryId || undefined,
          })
        );
      } else {
        toast.error(response.statusText, {
          position: "top-right",
          autoClose: 3000,
        });
      }

    } catch (error) {
      console.error("Error updating document status:", error);
      toast.error('Failed to update documents.', {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const categories = categoriesState?.data?.data || [];

  useEffect(() => {
    dispatch(fetchVehiclesCategoriesData());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchVehiclesDocumentsData({
        page: pageIndex + 1,
        limit: pageSize,
        vehicleCategoryId: vehicleCategoryId || undefined,
      })
    );
  }, [dispatch, pageIndex, pageSize, vehicleCategoryId]);

  const columns = useMemo(
    () => [
      {
        header: 'Driver',
        accessorKey: 'driver',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Vehicle',
        accessorKey: 'vehicle',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Vehicles Categories',
        accessorKey: 'vehicle_category',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          return (
            <button
              className="btn btn-sm btn-success"
              onClick={() => handleViewDocuments(row.original)}
            >
              <i className="mdi mdi-eye"></i> View
            </button>
          );
        }
      }
    ],
    []
  );

  const totalRecords = data?.total ?? data?.data?.length ?? 0;

  const handlePaginationChange = (newPageIndex, newPageSize) => {
    setPageIndex(newPageIndex);
    setPageSize(newPageSize);
  };

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      const categoryId =
        item.type_id ??
        item.vehicle_type_id ??
        item.vehicle_category_id ??
        item.vehicle?.type_id;
      const categoryFromList =
        categoryId != null
          ? categories.find((c) => String(c.id) === String(categoryId))?.displayname
          : null;
      // API returns vehicle_type_displayname from vehicle_types.displayname
      const vehicleCategory =
        item.vehicle_type_displayname ??
        categoryFromList ??
        item.type_displayname ??
        item.vehicle_type?.displayname ??
        item.vehicle?.vehicle_type?.displayname ??
        '—';
      return {
        ...item,
        driver: item.driver_name + ' - ' + item.driver_email,
        vehicle: item.vehicle_make_name + ' - ' + item.vehicle_license_plate,
        vehicle_category: vehicleCategory,
      };
    });
  }, [data, categories]);

  const handleVehicleCategoryFilterChange = (e) => {
    setVehicleCategoryId(e.target.value);
    setPageIndex(0);
  };

  document.title = "Vehicles Documents | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Documents (Vehicles)" />
        <Row className="mb-3">
          <Col md={4}>
            <Label className="form-label">Vehicles Categories</Label>
            <Input
              type="select"
              value={vehicleCategoryId}
              onChange={handleVehicleCategoryFilterChange}
            >
              <option value="">All</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.displayname}
                </option>
              ))}
            </Input>
          </Col>
        </Row>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            isCustomPageSize={true}
            SearchPlaceholder={`Search in ${totalRecords} results.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
            serverSidePagination={true}
            totalRowCount={totalRecords}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPaginationChange={handlePaginationChange}
          />
        )}

        {modalOpen && (
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Vehicle Documents</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex flex-wrap gap-3">
                    {selectedDriverDocuments.documents.map(doc => {
                      const isApproved = doc.status?.toLowerCase() === 'approved';
                      const isSelected = selectedDocIds.includes(doc.id);

                      return (
                        <div
                          key={doc.id}
                          className={`card p-2 ${isSelected ? 'border border-success' : ''} ${isApproved ? 'opacity-50' : ''}`}
                          style={{ width: "180px", cursor: isApproved ? "not-allowed" : "pointer", position: "relative" }}
                          onClick={() => {
                            if (!isApproved) toggleDocSelection(doc.id);
                          }}
                        >
                          {
                            isImage(doc.document) ? (
                              <img
                                src={doc.document}
                                alt={doc.document_name}
                                style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "6px" }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100px",
                                  borderRadius: "6px",
                                  backgroundColor: "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  color: "#333"
                                }}
                              >
                                {getFileExtension(doc.document).toUpperCase()} File
                              </div>
                            )
                          }
                          <div className="mt-2 text-center fw-semibold">{doc.document_name}</div>
                          <div>Uploaded at:<br/> {moment(doc.created_at).format("DD-MM-YYYY h:mm A")} </div>

                          {/* Status Badge */}
                          <div className={`badge bg-${getStatusColor(doc.status)} position-absolute top-0 end-0 m-1`}>
                            {doc.status}
                          </div>

                          {/* Input show only if NOT approved */}
                          {!isApproved && (
                          <input
                                type="text"
                                className="form-control form-control-sm mt-2"
                                placeholder="Enter remarks"
                                value={remarks[doc.id] || ""}
                                onChange={(e) => handleRemarkChange(doc.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                          )}

                          {isApproved && (
                          <input
                                type="text"
                                className="form-control form-control-sm mt-2"
                                placeholder="Enter remarks"
                                value={doc.remarks}
                                onChange={(e) => handleRemarkChange(doc.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                readOnly={isApproved}
                                disabled={isApproved}
                              />
                          )}

                          {/* View Button */}
                          <button
                            className="btn btn-sm btn-outline-primary mt-2"
                            style={{ width: "100%" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(doc.document, '_blank');
                            }}
                          >
                            View
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!areAllApproved() && 
                  <div className="modal-footer">
                    <button className="btn btn-success" onClick={() => handleActionClick("approved", selectedDriverDocuments.driver_id, selectedDriverDocuments.vehicle_id)}>Approve</button>
                    <button className="btn btn-danger" onClick={() => handleActionClick("rejected", selectedDriverDocuments.driver_id, selectedDriverDocuments.vehicle_id)}>Reject</button>
                  </div>
                }
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

VehiclesDocument.propTypes = {
  t: PropTypes.any
};

export default VehiclesDocument;